import { billingRefusalDetails, type BillingRefusalDetails } from './api-error-kind';

export const LOCAL_ERROR_HANDLING_HEADER = 'X-DS-Local-Error-Handling';

export type LocalErrorHandlingPurpose =
	| 'release-page'
	| 'client-startup'
	| 'usage'
	| 'purchase'
	| 'billing'
	| 'billing-summary'
	| 'project-copy'
	| 'voice-studio'
	| 'history-rail'
	| 'historical-content'
	| 'current-thread-content'
	| 'history-diff'
	| 'chat-stop-generation'
	| 'chat-interaction'
	| 'chat-question-set'
	| 'chat-usage'
	| 'chat-billing'
	| 'user-presence';

export type LocalErrorHandlingPolicy = {
	purpose: LocalErrorHandlingPurpose;
	suppressResponseToast: boolean;
	suppressNetworkToast: boolean;
	suppressGlobalError: boolean;
};

const POLICIES: Record<LocalErrorHandlingPurpose, LocalErrorHandlingPolicy> = {
	'release-page': {
		purpose: 'release-page',
		suppressResponseToast: true,
		suppressNetworkToast: true,
		suppressGlobalError: true,
	},
	'client-startup': {
		purpose: 'client-startup',
		suppressResponseToast: true,
		suppressNetworkToast: true,
		suppressGlobalError: true,
	},
	usage: {
		purpose: 'usage',
		suppressResponseToast: false,
		suppressNetworkToast: false,
		suppressGlobalError: false,
	},
	// Buying Dream Bucks shows its own inline errors (out-of-bounds amount, purchases off,
	// billing not set up, provider down) and treats a refused poll as "keep waiting".
	purchase: {
		purpose: 'purchase',
		suppressResponseToast: true,
		suppressNetworkToast: false,
		suppressGlobalError: false,
	},
	// The billing page shows its own inline states and messages (billing off or not set up,
	// provider down, a card that can't be removed), so API refusals don't also toast.
	billing: {
		purpose: 'billing',
		suppressResponseToast: true,
		suppressNetworkToast: false,
		suppressGlobalError: false,
	},
	// The billing page's primary read (`throwOnError`): a billing refusal (402) comes back as a
	// typed error for the page to show inline, anything else (e.g. 403) is the usual full-page error.
	'billing-summary': {
		purpose: 'billing-summary',
		suppressResponseToast: true,
		suppressNetworkToast: false,
		suppressGlobalError: false,
	},
	'project-copy': {
		purpose: 'project-copy',
		suppressResponseToast: true,
		suppressNetworkToast: true,
		suppressGlobalError: true,
	},
	'voice-studio': {
		purpose: 'voice-studio',
		suppressResponseToast: true,
		suppressNetworkToast: false,
		suppressGlobalError: false,
	},
	'history-rail': {
		purpose: 'history-rail',
		suppressResponseToast: true,
		suppressNetworkToast: true,
		suppressGlobalError: true,
	},
	'historical-content': {
		purpose: 'historical-content',
		suppressResponseToast: true,
		suppressNetworkToast: true,
		suppressGlobalError: true,
	},
	// Identifies Current tree reads so the interceptor can defer only the specific
	// archived-content conflict to the loader. All other errors keep normal handling.
	'current-thread-content': {
		purpose: 'current-thread-content',
		suppressResponseToast: false,
		suppressNetworkToast: false,
		suppressGlobalError: false,
	},
	'history-diff': {
		purpose: 'history-diff',
		suppressResponseToast: true,
		suppressNetworkToast: true,
		suppressGlobalError: true,
	},
	// Stopping an answer that already finished returns a benign 409 whose body is
	// the finalized message; suppress the "There was an error" response toast, but
	// keep normal network/global handling for genuine failures.
	'chat-stop-generation': {
		purpose: 'chat-stop-generation',
		suppressResponseToast: true,
		suppressNetworkToast: false,
		suppressGlobalError: false,
	},
	// Interacting with a content block that was already answered returns a benign
	// 409 whose body is the existing message with the recorded interaction; same
	// treatment as chat-stop-generation.
	'chat-interaction': {
		purpose: 'chat-interaction',
		suppressResponseToast: true,
		suppressNetworkToast: false,
		suppressGlobalError: false,
	},
	'chat-question-set': {
		purpose: 'chat-question-set',
		suppressResponseToast: true,
		suppressNetworkToast: false,
		suppressGlobalError: false,
	},
	// The chat's Dream Bucks figure is a nicety: with billing off, no usage permission or Metronome
	// down, the chat just shows no figure, without a toast or an error page.
	'chat-usage': {
		purpose: 'chat-usage',
		suppressResponseToast: true,
		suppressNetworkToast: true,
		suppressGlobalError: true,
	},
	// The organization's name under a billing refusal is a nicety: without it the notice says
	// "your organization".
	'chat-billing': {
		purpose: 'chat-billing',
		suppressResponseToast: true,
		suppressNetworkToast: true,
		suppressGlobalError: true,
	},
	'user-presence': {
		purpose: 'user-presence',
		suppressResponseToast: true,
		suppressNetworkToast: true,
		suppressGlobalError: true,
	},
};

export class UsageUnavailableError extends Error {
	readonly status = 402;
	constructor(
		readonly kind?: string,
		/** For `BILLING_NOT_PROVISIONED`: the payer without a contract, and whether it can set up. */
		readonly details: BillingRefusalDetails = {},
	) {
		super('Usage is unavailable');
	}
}

export function usageUnavailableResponse(
	request: Pick<Request, 'headers'> | undefined,
	response: Pick<Response, 'status'> | undefined,
	kind?: string,
	body?: unknown,
): UsageUnavailableError | undefined {
	const purpose = localErrorHandlingPolicy(request)?.purpose;
	return response?.status === 402 && (purpose === 'usage' || purpose === 'billing-summary')
		? new UsageUnavailableError(kind, billingRefusalDetails(body))
		: undefined;
}

export function localErrorHandlingPolicy(
	request: Pick<Request, 'headers'> | undefined,
): LocalErrorHandlingPolicy | undefined {
	const value = request?.headers.get(LOCAL_ERROR_HANDLING_HEADER);
	return value && Object.hasOwn(POLICIES, value)
		? POLICIES[value as LocalErrorHandlingPurpose]
		: undefined;
}

export function withLocalErrorHandling(
	headers: HeadersInit | undefined,
	purpose: LocalErrorHandlingPurpose,
): Headers {
	const merged = new Headers(headers);
	merged.set(LOCAL_ERROR_HANDLING_HEADER, purpose);
	return merged;
}
